import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { boolean, number, object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import {
  FormikDateField,
  FormikDropdownField,
  FormikTextField,
} from '@components/FormikFields';
import InputLabel from '@components/InputLabel/index';
import RoundedButton from '@components/RoundedButton/index';

import { RoleName } from '@constants/role_name';

import {
  useCreateCuratorTask,
  useUpdateCuratorTask,
} from '@hooks/api/curator-task';
import { useGetAllPatientUsers, useGetAllUsers } from '@hooks/api/user';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';

import { formatClientId, formatName } from '@utils/format';

import type { GetAllCuratorTaskRecordsQuery } from 'generated/curator-task.generated';
import type { CuratorTaskModel } from 'generated/types';

import { RecordsUpdateStyle, TitleText } from './TaskModalContent.style';

const messages = defineMessages({
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  name: {
    id: 'GLOBAL.name',
    defaultMessage: 'Name',
  },
  client: {
    id: 'taskForm.client',
    defaultMessage: 'Client',
  },
  user: {
    id: 'taskForm.user',
    defaultMessage: 'Assigned User',
  },
  dueDate: {
    id: 'taskForm.dueDate',
    defaultMessage: 'Due Date',
  },
  status: {
    id: 'taskForm.status',
    defaultMessage: 'Status',
  },
  completed: {
    id: 'GLOBAL.completed',
    defaultMessage: 'Completed',
  },
  incomplete: {
    id: 'taskForm.incomplete',
    defaultMessage: 'Incomplete',
  },
  description: {
    id: 'taskForm.description',
    defaultMessage: 'Description',
  },
  priority: {
    id: 'taskForm.priority',
    defaultMessage: 'Priority',
  },
  // Priority
  lowestPriority: {
    id: 'taskForm.lowestPriority',
    defaultMessage: 'Lowest',
  },
  lowPriority: {
    id: 'taskForm.lowPriority',
    defaultMessage: 'Low',
  },
  mediumPriority: {
    id: 'taskForm.mediumPriority',
    defaultMessage: 'Medium',
  },
  highPriority: {
    id: 'taskForm.highPriority',
    defaultMessage: 'High',
  },
  highestPriority: {
    id: 'taskForm.highestPriority',
    defaultMessage: 'Highest',
  },
});

type TaskFormFormikProps = Pick<
  CuratorTaskModel,
  'due_date' | 'name' | 'priority' | 'status' | 'user_id' | 'vitall_patient_id'
> & {
  description?: string | null;
};

type ITask = ArrayElement<
  GetAllCuratorTaskRecordsQuery['getAllCuratorTaskRecords']['data']
>;

const TaskModalContent: React.FC<{
  /**
   * Callback for when the submit button is clicked
   */
  onSubmit?: () => void;
  /**
   * Task to be edited
   */
  task?: ITask;
  /**
   * Task modal used for client profile screen
   */
  profileView?: boolean;
  /**
   * ID of vitall patient's curator user id
   */
  curator_id?: string | null;
}> = ({ onSubmit, task, profileView, curator_id }) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile, loggedInProfile },
  } = useSession();

  const { data: patientsData, loading: patientsLoading } =
    useGetAllPatientUsers();
  const { data: usersData, loading: usersLoading } = useGetAllUsers();
  const [createTask, createTaskLoading] = useCreateCuratorTask();
  const [updateTask, updateTaskLoading] = useUpdateCuratorTask();

  const loading =
    patientsLoading || createTaskLoading || updateTaskLoading || usersLoading;

  const TaskFormSchema = object().shape({
    name: string().required(formatMessage(messages.required)),
    // Assigned user
    user_id: string().required(formatMessage(messages.required)),
    // Patient
    vitall_patient_id: string().required(formatMessage(messages.required)),
    account_id: string(),
    due_date: string().required(formatMessage(messages.required)),
    status: boolean().required(formatMessage(messages.required)),
    description: string().nullable(),
    priority: number(),
  });

  const patientOptions = (patientsData ?? [])
    .slice()
    .sort(({ first_name: nameA }, { first_name: nameB }) =>
      nameA.localeCompare(nameB)
    )
    .map(({ first_name, last_name, vitall_patient, vitall_user_id }) => ({
      k: vitall_patient?.vitall_patient_id ?? '',
      v: `${formatName(null, first_name, last_name, null)} (${formatClientId(
        vitall_user_id ?? '',
        false
      )})`,
    }));

  const assigneeOptions = usersData
    ?.slice()
    .filter((a) => a.roles.some((rol) => rol.role_name !== RoleName.Client))
    .sort((a, b) => a.first_name.localeCompare(b.first_name))
    .map(({ first_name, last_name, user_id }) => ({
      k: user_id,
      v: formatName(null, first_name, last_name, null) ?? '-',
    }));

  const PRIORITY_OPTIONS = [
    {
      k: 1,
      v: formatMessage(messages.lowestPriority),
    },
    {
      k: 2,
      v: formatMessage(messages.lowPriority),
    },
    {
      k: 3,
      v: formatMessage(messages.mediumPriority),
    },
    {
      k: 4,
      v: formatMessage(messages.highPriority),
    },
    {
      k: 5,
      v: formatMessage(messages.highestPriority),
    },
  ];

  async function handleSubmit(values: TaskFormFormikProps) {
    try {
      if (task) {
        await updateTask({
          input: {
            curator_task_id: task.curator_task_id,
            curator_task: {
              // ...values,
              name: values.name,
              due_date: values.due_date,
              status: values.status,
              description: values.description,
              priority: Number(values.priority),
              vitall_patient_id: task.vitall_patient_id,
              user_id: profileView ? (curator_id ?? '') : values.user_id,
            },
          },
        });
      } else {
        await createTask({
          input: {
            ...values,
            priority: Number(values.priority),
            status: false,
            created_user_id: loggedInProfile?.user_id ?? '',
            description: values.description ?? '',
            vitall_patient_id: profileView
              ? (activeProfile?.vitall_patient_id ?? '')
              : values.vitall_patient_id,
            user_id: profileView ? (curator_id ?? '') : values.user_id,
          },
        });
      }
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      onSubmit?.();
    }
  }

  return (
    <RecordsUpdateStyle>
      <TitleText>{`${task ? 'Edit' : 'Add'} Task`}</TitleText>
      <HorizontalRule />
      <ContentLoader lineCount={1} loading={loading}>
        <Formik
          initialValues={{
            name: task?.name ?? '',
            user_id: (profileView ? curator_id : task?.user_id) ?? '',
            vitall_patient_id:
              (profileView
                ? activeProfile?.vitall_patient_id
                : task?.vitall_patient_id) ?? '',
            due_date: task?.due_date ?? '',
            status: task?.status ?? false,
            description: task?.description ?? '',
            priority: task?.priority ?? 3,
          }}
          onSubmit={handleSubmit}
          validationSchema={TaskFormSchema}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Form>
              <FormikTextField
                label={formatMessage(messages.name)}
                name="name"
                required
              />
              <FormikDropdownField
                label={formatMessage(messages.client)}
                labelColor="primary.main"
                name="vitall_patient_id"
                onChange={(value) => {
                  const patient = patientsData?.find(
                    ({ vitall_patient }) =>
                      vitall_patient?.vitall_patient_id === value
                  );
                  setFieldValue(
                    'user_id',
                    patient?.vitall_patient?.curator_user_id ?? ''
                  );
                }}
                options={patientOptions}
                required
              />
              <FormikDropdownField
                label={formatMessage(messages.user)}
                labelColor="primary.main"
                name="user_id"
                options={assigneeOptions}
                required
              />
              <FormikDateField
                label={formatMessage(messages.dueDate)}
                name="due_date"
                onChange={(value) => setFieldValue('due_date', value)}
                required
              />
              <FormikTextField
                label={formatMessage(messages.description)}
                name="description"
                type="text-area"
              />
              <FormikDropdownField
                label={formatMessage(messages.priority)}
                labelColor="primary.main"
                name="priority"
                options={PRIORITY_OPTIONS}
                required
              />
              {task ? (
                <>
                  <InputLabel
                    label={formatMessage(messages.status)}
                    style={{ color: 'primary.main' }}
                  />
                  <RoundedButton
                    height="30px"
                    onClick={() => setFieldValue('status', !values.status)}
                    squareShape
                    type="button"
                    variant="grey"
                  >
                    {formatMessage(
                      values.status ? messages.completed : messages.incomplete
                    )}
                  </RoundedButton>
                </>
              ) : null}

              <RoundedButton disabled={isSubmitting} type="submit">
                {formatMessage(messages.submit)}
              </RoundedButton>
            </Form>
          )}
        </Formik>
      </ContentLoader>
    </RecordsUpdateStyle>
  );
};

export default TaskModalContent;
